<script>
import Modal from "@/components/g-modal.vue";
import Field from "@/components/input-v.vue";
import Table from "@/components/table-v.vue";
import { mapGetters } from "vuex";
import { ROADMAP_STATUS_OPTIONS, ROADMAP_STATUS_EM_ATRASO } from "./roadmap.constants";

export default {
  components: { Field, Modal, Table },
  props: {
    canEditStatus: Boolean,
    editable: Boolean,
    hasAdminRole: Boolean,
    hasDiscovery: { type: Boolean, default: false },
    isOficial: { type: Boolean, default: false },
    platforms: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] }
  },
  data() {
    return {
      createDiscoveryData: {},
      createDiscoveryModalOpened: false,
      updateDiscoveryModalOpened: false,
      createRoadmapData: {},
      createRoadmapModalOpened: false,
      updateRoadmapModalOpened: false,
      editStatusData: { ids: [], status: null },
      editStatusModalOpened: false,
      error: 0,
      selectedRoadmaps: [],
      statusOptions: ROADMAP_STATUS_OPTIONS
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    actionBarButtons() {
      const buttons = [
        {
          text: "Atualizar",
          icon: "mdi-refresh",
          action: () => {
            this.doLoad();
          }
        }
      ];

      const canEdit = this.editable || this.hasAdminRole;

      if (this.isOficial && this.canEditStatus && this.selectedRoadmaps.length > 0) {
        buttons.push({
          text: `Editar Status (${this.selectedRoadmaps.length})`,
          icon: "mdi-tag-edit-outline",
          action: () => {
            this.editStatusData = {
              ids: this.selectedRoadmaps.map(r => r.id),
              status: null
            };
            this.editStatusModalOpened = true;
          }
        });
      }

      if (!this.hasDiscovery && canEdit) {
        buttons.push({
          text: "Adicionar Discovery",
          icon: "mdi-plus-box-outline",
          action: () => {
            this.createDiscoveryData = {};
            this.createDiscoveryModalOpened = true;
          }
        });
      } else if (this.hasDiscovery && canEdit) {
        buttons.push({
          text: "Adicionar Roadmap",
          icon: "mdi-plus-box-outline",
          action: () => {
            this.createRoadmapData = {};
            this.createRoadmapModalOpened = true;
          }
        });
      }

      return buttons;
    },
    cols() {
      return [
        {
          key: "entregavel",
          name: "Item",
          type: this.$fieldTypes.TEXT
        },
        {
          key: "nome",
          name: "Entregável",
          type: this.$fieldTypes.TEXT
        },
        {
          key: "plataforma_id",
          name: "Plataforma",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "platforms", key: "id", name: "plataforma" }
        },
        {
          key: "status",
          name: "Status",
          type: this.$fieldTypes.SELECT,
          rel: { to: "statusOptions", key: "id", name: "name" },
          colSize: 6
        },
        {
          key: "epico",
          name: "Épico",
          type: this.$fieldTypes.NUMBER,
          rules: this.epicoRules,
          colSize: 3
        },
        {
          key: "descricao",
          name: "Descrição",
          type: this.$fieldTypes.TEXT,
          tooltip: true,
          width: "300px"
        },
        {
          key: "data_inicio",
          name: "Data Início",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        },
        {
          key: "data_fim",
          name: "Data Fim",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        }
      ];
    },
    discoveryFields() {
      return [
        {
          key: "data_inicio",
          name: "Data Inicio",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        },
        {
          key: "data_fim",
          name: "Data Fim",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        }
      ];
    },
    epicoRules() {
      return [{ rule: "max", params: { size: 9999999 } }];
    },
    hasOverdueItems() {
      return this.parsedRows.some(row => row.status === ROADMAP_STATUS_EM_ATRASO);
    },
    parsedRows() {
      if (!Array.isArray(this.rows)) {
        return [];
      }

      // `isDiscovery` preserva a marcação original (entregavel === 0) antes de
      // reindexar `entregavel` para exibição, pois o Discovery não participa da
      // edição de Status (nem individual, nem em lote).
      return this.rows.map((row, index) => ({
        ...row,
        isDiscovery: row.entregavel === 0,
        entregavel: index + 1
      }));
    },
    editStatusFields() {
      return [
        {
          key: "status",
          name: "Status",
          type: this.$fieldTypes.SELECT,
          rel: { to: "statusOptions", key: "id", name: "name" },
          rules: [{ rule: "required" }],
          colSize: 12
        }
      ];
    },
    roadmapFields() {
      return [
        {
          key: "nome",
          name: "Entregável",
          rules: [{ rule: "required" }, { rule: "max", params: { size: 255 } }],
          type: this.$fieldTypes.TEXT,
          colSize: 6
        },
        {
          key: "plataforma_id",
          name: "Plataforma",
          rules: [{ rule: "required" }],
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "platforms", key: "id", name: "plataforma" },
          colSize: 6
        },
        {
          key: "epico",
          name: "Épico",
          type: this.$fieldTypes.NUMBER,
          rules: this.epicoRules,
          colSize: 6
        },
        {
          key: "descricao",
          name: "Descrição detalhada das principais atividades relacionados ao entregável",
          rules: [
            { rule: "required" },
            { rule: "max", params: { size: 6000 } }
          ],
          type: this.$fieldTypes.TEXTAREA,
          colSize: 12
        },
        {
          key: "data_inicio",
          name: "Data Início",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        },
        {
          key: "data_fim",
          name: "Data Fim",
          rules: [{ rule: "required" }],
          disableDefaultDatepickerValue: true,
          type: this.$fieldTypes.DATE,
          colSize: 6
        }
      ];
    }
  },
  methods: {
    classesByRow(row) {
      return { "row-em-atraso": row.status === ROADMAP_STATUS_EM_ATRASO };
    },
    contextmenu(row, event, _, col) {
      const canEdit = this.editable || this.hasAdminRole;
      const canEditStatus = this.isOficial && this.canEditStatus;

      if (!canEdit && !canEditStatus) {
        return;
      }
      setTimeout(() => {
        const isDiscovery = row.isDiscovery === true;
        const menu = [];

        if (canEdit) {
          menu.push(
            {
              name: "Editar",
              icon:
                "v-icon notranslate mr-3 mdi mdi-square-edit-outline theme--light",
              cb: row => {
                if (isDiscovery) {
                  this.createDiscoveryData = { ...row };
                  this.updateDiscoveryModalOpened = true;
                } else {
                  this.createRoadmapData = { ...row };
                  this.updateRoadmapModalOpened = true;
                }
              }
            },
            {
              name: "Excluir",
              icon:
                "v-icon notranslate mr-3 mdi mdi-trash-can-outline theme--light",
              cb: row => {
                this.$emit("delete", row.id);
              }
            }
          );
        }

        if (canEditStatus && !isDiscovery) {
          menu.push({
            name: "Editar Status",
            icon:
              "v-icon notranslate mr-3 mdi mdi-tag-edit-outline theme--light",
            cb: row => {
              this.editStatusData = { ids: [row.id], status: row.status };
              this.editStatusModalOpened = true;
            }
          });
        }

        if (!menu.length) {
          return;
        }

        this.showContext({
          left: event.clientX,
          top: event.clientY,
          val: row,
          col,
          menu
        });
      }, 100);
    },
    doLoad() {
      this.$emit("load");
    },
    doSaveStatus(close) {
      const invalidFields = this.editStatusFields.filter(
        ({ valid }) => !!valid && !valid()
      );

      if (invalidFields.length) {
        return;
      }

      this.$emit("save-status", {
        ids: this.editStatusData.ids,
        status: this.editStatusData.status,
        close
      });
    },
    doSaveDiscovery(close) {
      const invalidFields = this.discoveryFields.filter(
        ({ valid }) => !!valid && !valid()
      );

      if (invalidFields.length) {
        return;
      }

      this.$emit("save", { data: this.createDiscoveryData, close });
    },
    doSaveRoadmap(close) {
      const invalidFields = this.roadmapFields.filter(
        ({ valid }) => !!valid && !valid()
      );

      if (invalidFields.length) {
        return;
      }

      this.$emit("save", { data: this.createRoadmapData, close });
    },
    validate(mode = "save") {
      if (mode === "approve" && !this.rows.length) {
        this.error = 1;
        this.$emit("error", 1);
        return false;
      }

      this.error = 0;
      this.$emit("error", 0);
      return true;
    }
  },
  watch: {
    editStatusModalOpened(val) {
      if (!val) {
        this.selectedRoadmaps = [];
      }
    },
    selectedRoadmaps(val) {
      // O Discovery não participa da edição de Status em lote. Caso seja
      // selecionado (ex.: via "selecionar todos"), remove-o imediatamente — o
      // checkbox da linha volta a ficar desmarcado pela sincronia da seleção.
      const semDiscovery = val.filter(row => !row.isDiscovery);
      if (semDiscovery.length !== val.length) {
        this.selectedRoadmaps = semDiscovery;
      }
    },
    rows() {
      if (this.error > 0) {
        this.validate("approve");
      }
    }
  }
};
</script>

<template>
  <div class="roadmaps-wrapper">
    <v-alert
      v-if="hasOverdueItems"
      class="mb-2"
      color="error"
      dense
      icon="mdi-alert-circle-outline"
      text
      type="error"
    >
      Há entregáveis <strong>em atraso</strong> no Roadmap deste Business Plan.
    </v-alert>

    <Table
      :actionBarButtons="actionBarButtons"
      class="roadmaps-table"
      :classesByRow="classesByRow"
      :cols="cols"
      disablePagination
      :hasExportCSV="false"
      :hasExportXLSX="false"
      :hasFilter="false"
      height="auto"
      :opts="{ platforms, statusOptions }"
      :rows="parsedRows"
      :selection.sync="selectedRoadmaps"
      selectionCol
      @contextmenu="contextmenu"
    >
      <Modal
        :opened="createDiscoveryModalOpened || updateDiscoveryModalOpened"
        :title="
          `${createDiscoveryModalOpened ? 'Cadastro' : 'Edição'} do Discovery`
        "
        @update:opened="
          createDiscoveryModalOpened = $event;
          updateDiscoveryModalOpened = $event;
        "
      >
        <v-container>
          <v-row>
            <Field
              v-for="(field, i) of discoveryFields"
              v-model="createDiscoveryData[field.key]"
              :block.sync="field.block"
              class="pl-4"
              :colSize="field.colSize"
              :key="i"
              :label="field"
              :opts="{}"
              :valid.sync="field.valid"
            />
          </v-row>
        </v-container>

        <template #buttons="{ close }">
          <v-spacer />
          <v-btn class="px-5 ml-3" color="secondary" depressed @click="close()">
            Cancelar
          </v-btn>
          <v-btn
            class="px-5 ml-3"
            color="primary"
            dark
            depressed
            @click="doSaveDiscovery(close)"
          >
            Salvar
          </v-btn>
        </template>
      </Modal>

      <Modal
        :opened="createRoadmapModalOpened || updateRoadmapModalOpened"
        :title="
          `${createRoadmapModalOpened ? 'Cadastro' : 'Edição'} de entregável`
        "
        width="900"
        @update:opened="
          createRoadmapModalOpened = $event;
          updateRoadmapModalOpened = $event;
        "
      >
        <v-container v-if="createRoadmapModalOpened || updateRoadmapModalOpened">
          <v-row>
            <Field
              v-for="(field, i) of roadmapFields"
              v-model="createRoadmapData[field.key]"
              :block.sync="field.block"
              class="pl-4"
              :colSize="field.colSize"
              :key="i"
              :label="field"
              :opts="{ platforms, statusOptions }"
              :valid.sync="field.valid"
            />
          </v-row>
        </v-container>

        <template #buttons="{ close }">
          <v-spacer />
          <v-btn class="px-5 ml-3" color="secondary" depressed @click="close()">
            Cancelar
          </v-btn>
          <v-btn
            class="px-5 ml-3"
            color="primary"
            dark
            depressed
            @click="doSaveRoadmap(close)"
          >
            Salvar
          </v-btn>
        </template>
      </Modal>
      <Modal
        :opened="editStatusModalOpened"
        title="Editar Status"
        width="500"
        @update:opened="editStatusModalOpened = $event"
      >
        <v-container v-if="editStatusModalOpened">
          <v-row>
            <Field
              v-for="(field, i) of editStatusFields"
              v-model="editStatusData[field.key]"
              :block.sync="field.block"
              class="pl-4"
              :colSize="field.colSize"
              :key="i"
              :label="field"
              :opts="{ statusOptions }"
              :valid.sync="field.valid"
            />
          </v-row>
        </v-container>

        <template #buttons="{ close }">
          <v-spacer />
          <v-btn class="px-5 ml-3" color="secondary" depressed @click="close()">
            Cancelar
          </v-btn>
          <v-btn
            class="px-5 ml-3"
            color="primary"
            dark
            depressed
            @click="doSaveStatus(close)"
          >
            Salvar
          </v-btn>
        </template>
      </Modal>
    </Table>
  </div>
</template>

<style lang="scss" scoped>
.roadmaps-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.roadmaps-table {
  flex: 1;
  min-height: 0;

  &::v-deep.v-card {
    height: 100%;
    display: flex;
    flex-direction: column;

    .v-card__text {
      flex-shrink: 0;
    }

    .v-data-table {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .v-data-table__wrapper {
      flex-grow: 1;
      overflow: auto;
    }

    .v-data-footer {
      flex-shrink: 0;
    }
  }
}

::v-deep .row-em-atraso td {
  background-color: #fdecea !important;
  color: #b71c1c;
}
</style>
